import { RefObject } from "react";
import {
  useOpenCloseTransitionStates,
  type OpenCloseTransitionStateEffects,
} from "./useOpenCloseTransitionStates";

/**
 * Effects configuration for dialog transition states
 * @property {() => void} [onOpenAnimationStart] - Callback when opening animation starts
 * @property {() => void} [onCloseAnimationStart] - Callback when closing animation starts
 * @property {() => void} [onOpenAnimationComplete] - Callback when opening animation completes
 * @property {() => void} [onCloseAnimationComplete] - Callback when closing animation completes
 */

/**
 * Custom hook for managing dialog transition states and animations.
 *
 * Features:
 * - Manages dialog open/close transition states
 * - Handles animation timing based on CSS animation duration
 * - Provides callbacks for animation lifecycle events
 * - Supports controlled dialog state management
 * - Automatically handles showModal() and close() calls
 * - Provides utility flags for open/closed states
 *
 * @param ref - The ref to the dialog element
 * @param openProp - The open prop controlling dialog visibility
 * @param options - Additional, optional options including callbacks for animation start, complete, and initialization
 * @returns Object containing dialog state, control functions, and utility flags
 */

/**
 * Effects configuration for dialog transition states
 * @param openProp - The open prop controlling dialog visibility
 * @param options - The effects configuration
 * @returns The dialog transition state
 */
export const useDialogTransitionStates = (
  ref: RefObject<HTMLDialogElement>,
  openProp: boolean | undefined,
  options: OpenCloseTransitionStateEffects = {},
) => {
  const { onOpenAnimationStart, onCloseAnimationComplete } = options;
  return useOpenCloseTransitionStates(openProp, {
    ...options,
    onOpenAnimationStart: () => {
      if (!ref.current) return;
      ref.current.showModal();
      onOpenAnimationStart?.();
    },
    onCloseAnimationComplete: () => {
      if (!ref.current) return;
      ref.current.close();
      onCloseAnimationComplete?.();
    },
    durationMs: () => {
      if (!ref.current) return 0;
      const duration = window.getComputedStyle(ref.current).animationDuration;
      return parseFloat(duration) * 1000;
    },
  });
};
